import React, { FC, useEffect, useMemo, useRef } from 'react'

import clsx from 'clsx'
import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { useHoverDirty } from 'react-use'
import styled, { css } from 'styled-components'

import { Txt } from 'src/components/atoms'

dayjs.extend(isSameOrBefore)

const DayContainer = styled.div<{ weekday: number }>`
  // grid-column-startは1〜7の値を指定する必要があり0をweekdayの0を指定すると正しくスタイルがかからない
  grid-column-start: ${({ weekday }) => (weekday === 0 ? 7 : weekday)};
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`
const Cell = styled.div<{ cellType: CalendarDayCellType }>`
  position: absolute;
  width: 100%;
  height: 100%;

  ${({ cellType }) =>
    cellType !== 'none' &&
    css`
      background-color: rgba(242, 245, 249, 0.5);
    `}
  ${({ cellType }) =>
    cellType === 'left' &&
    css`
      width: 50%;
      left: 0;
    `}
  ${({ cellType }) =>
    cellType === 'right' &&
    css`
      width: 50%;
      right: 0;
    `}
`
const CircleContainer = styled.div<{ isMon: boolean; isSun: boolean }>`
  position: relative;
  ${({ isMon }) =>
    isMon &&
    css`
      padding-left: 12px;
    `};
  ${({ isSun }) =>
    isSun &&
    css`
      padding-right: 12px;
    `};
`

export type CalendarDayCellType = 'left' | 'right' | 'full' | 'none'

type Props = {
  day: dayjs.Dayjs
  isSelected: boolean
  cellType?: CalendarDayCellType
  onClick: (day: dayjs.Dayjs) => void
  onHover?: (day: dayjs.Dayjs | undefined) => void
  disabledBeforeDate?: dayjs.Dayjs
  disabledAfterDate?: dayjs.Dayjs
}

export const CalendarDay: FC<Props> = ({
  day,
  isSelected,
  cellType = 'none',
  onClick,
  onHover,
  disabledBeforeDate,
  disabledAfterDate,
}) => {
  const ref = useRef(null)
  const isHovering = useHoverDirty(ref)

  const handleOnClickDay = (d: dayjs.Dayjs) => {
    if (
      (disabledBeforeDate && !d.isSameOrAfter(disabledBeforeDate)) ||
      (disabledAfterDate && !d.isSameOrBefore(disabledAfterDate))
    ) {
      return
    }
    onClick(d)
  }

  useEffect(() => {
    if (!isHovering || day.isAfter(dayjs())) {
      onHover?.(undefined)
    } else {
      onHover?.(day)
    }
  }, [day, isHovering, onHover])

  const isDisabled = useMemo(
    () =>
      (disabledBeforeDate && day.isSameOrBefore(disabledBeforeDate, 'day')) ||
      (disabledAfterDate && day.isSameOrAfter(disabledAfterDate, 'day')),
    [day, disabledAfterDate, disabledBeforeDate]
  )

  return (
    <DayContainer key={day.toString()} weekday={day.day()} ref={ref}>
      <Cell cellType={cellType} />
      <CircleContainer isSun={day.day() === 0} isMon={day.day() === 1}>
        <button
          type="button"
          disabled={isDisabled}
          onClick={() => handleOnClickDay(day)}
          className={clsx(
            'tw-centering tw-w-[28px] tw-aspect-square tw-rounded-full',
            !isDisabled && (isSelected || isHovering) && 'tw-bg-surface-secondary'
          )}
        >
          <Txt
            fontType="alphabet"
            className={clsx(
              'tw-font-light',
              day.format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD')
                ? '!tw-font-bold tw-text-correctGreen-default'
                : '',
              isDisabled && 'tw-text-standardText-disable'
            )}
          >
            {day.format('D')}
          </Txt>
        </button>
      </CircleContainer>
    </DayContainer>
  )
}
